// pages/admin/agents/AgentsPage.tsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, ChevronLeft, ChevronRight, Mail, Eye, Edit, Trash2, List } from "lucide-react";
import PageListLayout from "../../../layouts/PageListLayout";
import InviteAgentModal from "../../../components/features/invitations/InviteAgentModal";
import useAuth from "../../../hooks/features/auth/useAuth";
import useAgents from "../../../hooks/features/agents/useAgents";
import { Agent } from "../../../types/features/Agent";
import { alertSuccess, alertError } from "../../../helpers/alertError";
import AgentList from "../../../components/features/agents/AgentsList";
import MenuModal, { Menu } from "../../../components/ui/MenuModal";
import DeleteConfirmationModal from "../../../components/ui/DeleteConfirmationModal";

export default function AgentsPage() {
  const navigate = useNavigate();
  const { agents, loading, pagination, getAgents, deleteAgent } = useAgents();
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [agentToDelete, setAgentToDelete] = useState<Agent | null>(null);
  const perPage = 10;

  useEffect(() => {
    loadAgents();
  }, [currentPage]);

  const loadAgents = async () => {
    await getAgents(currentPage, perPage);
  };

  const filteredAgents = agents.filter((agent) => {
    const search = searchTerm.toLowerCase();
    return (
      agent.full_name?.toLowerCase().includes(search) ||
      agent.email?.toLowerCase().includes(search) ||
      agent.phonenumber?.includes(search)
    );
  });

  const handleDelete = async () => {
    try {
      await deleteAgent(agentToDelete?.id!);
      alertSuccess("Agent supprimé avec succès");
      loadAgents();
    } catch (error) {
      alertError("Erreur lors de la suppression");
    } finally {
      setIsDeleting(false);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const getActionsMenu = (agent: Agent): Menu[] => {
    return [
      {
        label: 'Voir détails',
        icon: Eye,
        onClick: () => {
          navigate("/entreprise/agents/details", { state: agent });
        }
      },
      {
        label: "Modifier",
        icon: Edit,
        onClick: () => {
          navigate("/entreprise/agents/update", { state: agent });
        },
      },
      {
        label: "Supprimer",
        icon: Trash2,
        onClick: () => {
          setAgentToDelete(agent);
        },
      },
    ];
  };

  if (loading && agents.length === 0) {
    return (
      <PageListLayout title="Gestion des agents">
        <div className="flex items-center justify-center h-64 select-none animate-fadeIn">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </PageListLayout>
    );
  }

  return (
    <>
      <PageListLayout
        title="Gestion des agents"
        actions={
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/entreprise/invitations")}
              className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors shadow-sm font-medium"
            >
              <List className="w-4 h-4" />
              Voir les invitations
            </button>
            <button
              onClick={() => setIsInviteModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors shadow-sm font-medium"
            >
              <Mail className="w-4 h-4" />
              Inviter un agent
            </button>
          </div>
        }
      >
        <div className="p-6 select-none animate-fadeIn">
          {/* Search */}
          <div className="relative max-w-md mb-6">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Rechercher un agent..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all"
            />
          </div>

          {/* Agent List */}
          <AgentList
            agents={filteredAgents}
            onAction={(agent) => {
              setSelectedAgent(agent);
            }}
            selectable={true}
          />

          {/* Pagination */}
          {pagination.total > 0 && (
            <div className="flex items-center justify-between mt-6">
              <p className="text-sm text-gray-500">
                Page {pagination.currentPage} sur {pagination.lastPage} ({pagination.total} agents)
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 text-gray-600 border border-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handlePageChange(Math.min(pagination.lastPage, currentPage + 1))}
                  disabled={currentPage === pagination.lastPage}
                  className="px-4 py-2 text-gray-600 border border-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </PageListLayout>

      {/* Invite Modal */}
      <InviteAgentModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onSuccess={loadAgents}
      />

      <DeleteConfirmationModal
        isOpen={!!agentToDelete}
        onClose={() => setAgentToDelete(null)}
        onConfirm={handleDelete}
        title="Supprimer l'agent"
        message={`Êtes-vous sûr de vouloir supprimer l'agent "${agentToDelete?.full_name}" ? Cette action est irréversible.`}
        confirmText={isDeleting ? "Suppression..." : "Supprimer"}
        cancelText="Annuler"
      />

      {/* Menu modal volant (Actions d'un appel) */}
      {selectedAgent && (
        <MenuModal
          title={`Appel - ${selectedAgent.full_name || 'Inconnu'}`}
          isOpen={true}
          onClose={() => setSelectedAgent(null)}
          icon={null}
          menu={getActionsMenu(selectedAgent)}
        />
      )}
    </>
  );
}